import { useEffect, useMemo, useState } from "react";
import { AuthCptCode } from "../authTrackingData";

const PAGE_SIZE = 5;

export const STATIC_AUTH_SERVICES: AuthCptCode[] = [
  { id: "s-1", code: "70551", description: "MRI brain w/o contrast" },
  { id: "s-2", code: "70553", description: "MRI brain w/o & w/ contrast" },
  { id: "s-3", code: "70450", description: "CT head/brain w/o contrast" },
  { id: "s-4", code: "70460", description: "CT head/brain w/ contrast" },
  { id: "s-5", code: "72141", description: "MRI cervical spine w/o contrast" },
  { id: "s-6", code: "72148", description: "MRI lumbar spine w/o contrast" },
  { id: "s-7", code: "73721", description: "MRI joint of lower extremity" },
  { id: "s-8", code: "74177", description: "CT abdomen & pelvis w/ contrast" },
];

interface AuthServicesTableProps {
  services?: AuthCptCode[];
  resetKey?: string | number | boolean;
}

const AuthServicesTable = ({
  services,
  resetKey,
}: AuthServicesTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const rows = services?.length ? services : STATIC_AUTH_SERVICES;
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
  }, [resetKey, rows.length]);

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, currentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="card-content at-services-section">
      <h3 className="at-detail-section-title">Services</h3>

      <div className="table-responsive">
        <table className="table caption-top at-services-table">
          <thead>
            <tr>
              <th className="at-cpt-code-col">CPT codes</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <tr key={row.id}>
                <td className="at-cpt-code-col">{row.code}</td>
                <td>{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-center at-services-pagination">
        <nav
          aria-label="Services page navigation"
          className="d-flex align-items-center"
        >
          <span className="pagination-info me-2">
            Page{" "}
            <strong>
              {currentPage} of {totalPages}
            </strong>
          </span>
          <ul className="pagination d-flex justify-content-end mb-0">
            <li className="page-item">
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                <i className="fh_double_arrow_left"></i>
              </button>
            </li>
            <li className="page-item">
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="fh_arrow_left_line"></i>
              </button>
            </li>
            <li className="page-item">
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <i className="fh_arrow_right_line"></i>
              </button>
            </li>
            <li className="page-item">
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                <i className="fh_double_arrow_right"></i>
              </button>
            </li>
          </ul>
        </nav>
        <div>
          <select
            className="form-select form-control cursor-pointer custom-table-dropdown"
            value={PAGE_SIZE}
            disabled
            aria-label="Show page size"
          >
            {[5, 10, 20].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default AuthServicesTable;
